import { buscarEscritorioPorId } from '@/features/escritorio';
import type { NegociacaoIndividualWizardState } from '../types/wizardState';
import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { StartMode, WizardCounterparty } from '../types';
import {
  cancelNegociacaoIndividualStart,
  closePhoneConflictWizard,
  createNegociacaoIndividual,
  fetchNegociacaoIndividualAgent,
  fetchNegociacaoIndividualAgents,
  validatePhoneConflictWizard,
  validarCpfCnpjWizard,
} from './actions';

const initialState: NegociacaoIndividualWizardState = {
  workspaceId: null,
  agents: [],
  agentsPagination: { page: 0, limit: 20, total: 0, hasMore: false, search: '', requestId: null },
  isLoadingAgents: false,
  isLoadingAgentDetails: false,
  agentsError: null,

  selectedAgentId: null,
  selectedAgent: null,

  counterparty: {
    cpfCnpj: '',
    name: '',
    phone: '',
  },
  isValidatingDoc: false,
  docValidationDoc: null,
  docLookupError: null,
  docValidationData: null,

  title: '',
  objective: '',
  claimAmount: null,
  floorAmount: null,
  marginPercent: null,

  agentInput: {},

  startMode: 'NOW',
  scheduledDate: '',
  scheduledTime: '',

  extrajudicialItemId: null,

  isSubmitting: false,
  submitError: null,

  created: {
    caseId: null,
    conversaId: null,
    startEventId: null,
    startScheduledFor: null,
  },

  isCancellingStart: false,
  cancelStartError: null,

  phoneConflict: null,
  isValidatingPhoneConflict: false,
  phoneConflictError: null,
  isClosingPhoneConflict: false,
  closePhoneConflictError: null,
};

const negociacaoIndividualWizardSlice = createSlice({
  name: 'negociacaoIndividualWizard',
  initialState,
  reducers: {
    setSelectedAgentId: (state, action: PayloadAction<string | null>) => {
      state.selectedAgentId = action.payload;
      if (!action.payload) {
        state.selectedAgent = null;
      }
    },
    setCounterpartyField: (
      state,
      action: PayloadAction<{ field: keyof WizardCounterparty; value: string }>,
    ) => {
      const { field, value } = action.payload;
      const previousCpfCnpj = state.counterparty.cpfCnpj;
      state.counterparty = { ...state.counterparty, [field]: value };

      if (field === 'cpfCnpj' && value !== previousCpfCnpj) {
        state.isValidatingDoc = false;
        state.docValidationDoc = null;
        state.docLookupError = null;
        state.docValidationData = null;
      }
    },
    setAgentInputValue: (state, action: PayloadAction<{ key: string; value: unknown }>) => {
      state.agentInput[action.payload.key] = action.payload.value;
    },
    setTitle: (state, action: PayloadAction<string>) => {
      state.title = action.payload;
    },
    setObjective: (state, action: PayloadAction<string>) => {
      state.objective = action.payload;
    },
    setClaimAmount: (state, action: PayloadAction<number | null>) => {
      state.claimAmount = action.payload;
    },
    setFloorAmount: (state, action: PayloadAction<number | null>) => {
      state.floorAmount = action.payload;
    },
    setMarginPercent: (state, action: PayloadAction<number | null>) => {
      state.marginPercent = action.payload;
    },
    setStartMode: (state, action: PayloadAction<StartMode>) => {
      state.startMode = action.payload;
      if (action.payload === 'NOW') {
        state.scheduledDate = '';
        state.scheduledTime = '';
      }
    },
    setScheduledDate: (state, action: PayloadAction<string>) => {
      state.scheduledDate = action.payload;
    },
    setScheduledTime: (state, action: PayloadAction<string>) => {
      state.scheduledTime = action.payload;
    },
    setExtrajudicialItemId: (state, action: PayloadAction<string | null>) => {
      state.extrajudicialItemId = action.payload;
    },
    clearErrors: (state) => {
      state.agentsError = null;
      state.submitError = null;
      state.docLookupError = null;
      state.cancelStartError = null;
      state.phoneConflictError = null;
      state.closePhoneConflictError = null;
    },
    clearPhoneConflict: (state) => {
      state.phoneConflict = null;
      state.phoneConflictError = null;
      state.closePhoneConflictError = null;
    },
    resetWizard: (state) => ({ ...initialState, workspaceId: state.workspaceId }),
  },
  extraReducers: (builder) => {
    builder
      // Zera já no pending: esperar o fulfilled deixava a lista do escritório anterior selecionável
      // durante a troca (concorrência corrigida no PR #1374 da develop).
      .addCase(buscarEscritorioPorId.pending, (state, action) => {
        if (state.workspaceId !== action.meta.arg)
          return { ...initialState, workspaceId: action.meta.arg };
      })
      .addCase(fetchNegociacaoIndividualAgents.pending, (state, action) => {
        state.isLoadingAgents = true;
        state.agentsError = null;
        state.agentsPagination.requestId = action.meta.requestId;
        if (action.meta.arg.page === 1) {
          state.agents = [];
          state.agentsPagination.page = 0;
          state.agentsPagination.search = action.meta.arg.search ?? '';
          state.agentsPagination.hasMore = false;
        }
      })
      .addCase(fetchNegociacaoIndividualAgents.fulfilled, (state, action) => {
        if (state.agentsPagination.requestId !== action.meta.requestId) return;
        state.isLoadingAgents = false;
        const { items, ...pagination } = action.payload;
        const agents = action.meta.arg.page === 1 ? items : [...state.agents, ...items];
        state.agents = [...new Map(agents.map((agent) => [agent.id, agent])).values()];
        state.agentsPagination = {
          ...pagination,
          search: action.meta.arg.search ?? '',
          requestId: null,
        };
      })
      .addCase(fetchNegociacaoIndividualAgents.rejected, (state, action) => {
        if (state.agentsPagination.requestId !== action.meta.requestId) return;
        state.isLoadingAgents = false;
        state.agentsPagination.requestId = null;
        state.agentsError = action.payload || 'Erro ao carregar agentes';
      })

      .addCase(fetchNegociacaoIndividualAgent.pending, (state, action) => {
        state.isLoadingAgentDetails = true;
        state.selectedAgentId = action.meta.arg;
        state.selectedAgent = null;
        state.agentsError = null;
      })
      .addCase(fetchNegociacaoIndividualAgent.fulfilled, (state, action) => {
        if (state.selectedAgentId !== action.meta.arg) return;
        state.isLoadingAgentDetails = false;
        state.selectedAgent = action.payload;
      })
      .addCase(fetchNegociacaoIndividualAgent.rejected, (state, action) => {
        if (state.selectedAgentId !== action.meta.arg) return;
        state.isLoadingAgentDetails = false;
        state.agentsError = action.payload || 'Erro ao carregar detalhes do agente';
      })

      .addCase(validarCpfCnpjWizard.pending, (state, action) => {
        state.isValidatingDoc = true;
        state.docValidationDoc = action.meta.arg;
        state.docLookupError = null;
        state.docValidationData = null;
      })
      .addCase(validarCpfCnpjWizard.fulfilled, (state, action) => {
        const validatedDoc = action.meta.arg;

        if (!state.docValidationDoc || state.docValidationDoc !== validatedDoc) {
          return;
        }

        state.isValidatingDoc = false;
        state.docLookupError = null;
        state.docValidationData = action.payload;

        if (action.payload.fonte === 'database') {
          state.counterparty.name = action.payload.nome;
          return;
        }

        if (!state.counterparty.name) {
          state.counterparty.name = action.payload.nome;
        }
      })
      .addCase(validarCpfCnpjWizard.rejected, (state, action) => {
        const validatedDoc = action.meta.arg;

        if (!state.docValidationDoc || state.docValidationDoc !== validatedDoc) {
          return;
        }

        state.isValidatingDoc = false;
        state.docLookupError = action.payload || 'Erro ao validar CPF/CNPJ';
      })

      .addCase(createNegociacaoIndividual.pending, (state) => {
        state.isSubmitting = true;
        state.submitError = null;
        state.created = initialState.created;
      })
      .addCase(createNegociacaoIndividual.fulfilled, (state, action) => {
        state.isSubmitting = false;
        state.created = {
          caseId: action.payload.caseId,
          conversaId: action.payload.conversaId,
          startEventId: action.payload.startEventId,
          startScheduledFor: action.payload.startScheduledFor,
        };
      })
      .addCase(createNegociacaoIndividual.rejected, (state, action) => {
        state.isSubmitting = false;
        state.submitError = action.payload || 'Erro ao criar negociação';
      })

      .addCase(cancelNegociacaoIndividualStart.pending, (state) => {
        state.isCancellingStart = true;
        state.cancelStartError = null;
      })
      .addCase(cancelNegociacaoIndividualStart.fulfilled, (state) => {
        state.isCancellingStart = false;
      })
      .addCase(cancelNegociacaoIndividualStart.rejected, (state, action) => {
        state.isCancellingStart = false;
        state.cancelStartError = action.payload || 'Erro ao cancelar início';
      })

      .addCase(validatePhoneConflictWizard.pending, (state) => {
        state.isValidatingPhoneConflict = true;
        state.phoneConflictError = null;
      })
      .addCase(validatePhoneConflictWizard.fulfilled, (state, action) => {
        state.isValidatingPhoneConflict = false;
        state.phoneConflictError = null;
        state.phoneConflict = action.payload.hasConflict ? action.payload.conflict : null;
      })
      .addCase(validatePhoneConflictWizard.rejected, (state, action) => {
        state.isValidatingPhoneConflict = false;
        state.phoneConflictError = action.payload || 'Erro ao validar conflito por WhatsApp';
      })

      .addCase(closePhoneConflictWizard.pending, (state) => {
        state.isClosingPhoneConflict = true;
        state.closePhoneConflictError = null;
      })
      .addCase(closePhoneConflictWizard.fulfilled, (state, action) => {
        state.isClosingPhoneConflict = false;
        state.closePhoneConflictError = null;
        if (state.phoneConflict?.caseId === action.payload.caseId) {
          state.phoneConflict = null;
        }
      })
      .addCase(closePhoneConflictWizard.rejected, (state, action) => {
        state.isClosingPhoneConflict = false;
        state.closePhoneConflictError = action.payload || 'Erro ao encerrar negociação em conflito';
      });
  },
});

export const {
  clearPhoneConflict,
  resetWizard,
  setAgentInputValue,
  setClaimAmount,
  setCounterpartyField,
  setObjective,
  setScheduledDate,
  setScheduledTime,
  setSelectedAgentId,
  setStartMode,
  setTitle,
  setExtrajudicialItemId,
} = negociacaoIndividualWizardSlice.actions;

export default negociacaoIndividualWizardSlice.reducer;
